import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  BehaviorSubject,
  catchError,
  filter,
  finalize,
  map,
  Observable,
  share,
  switchMap,
  take,
  throwError,
} from 'rxjs';

import { Store } from '@ngxs/store';
import { environment } from '../../../environments/environment';
import { isPublicAuthRoute } from '../../auth/shared/public-auth-routes';
import { HomeState } from '../../modules/home/core/state/home.state';
import { AuthenticationService } from '../services/authentication/authentication.service';
import {
  AuthEndpoints,
  HorusEndpoints,
} from '../services/endpoints/core.endpoints';
import { MeProfile } from '../services/profile/me-profile.model';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private platformRefresh$: Observable<string> | null = null;
  private isRefreshingHorus = false;
  private refreshTokenHorusSubject: BehaviorSubject<string | null> =
    new BehaviorSubject<string | null>(null);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private readonly store = inject(Store);
  me = this.store.selectSignal<MeProfile>(HomeState.getMe);
  constructor(private authService: AuthenticationService) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    const isPlatformApi = req.url.startsWith(environment.baseUrl);
    const isHorusApi = req.url.startsWith(environment.horusBaseUrl);
    const isLprV2 = req.url.startsWith(environment.lprBaseUrlV2);
    const isLprDashboardApi = req.url.startsWith(environment.dashBoardApiUrl);
    const isPublicConfig = req.url.includes(
      'whitelabels/public/configurations',
    );

    if (!isPlatformApi && !isHorusApi && !isLprV2 && !isLprDashboardApi) {
      return next.handle(req);
    }

    if (isPublicConfig) {
      return next.handle(req);
    }

    if (isPlatformApi && this.isLoginFlowRequest(req.url)) {
      return next.handle(req);
    }

    if (
      req.url.endsWith(`/${AuthEndpoints.refresh}`) ||
      req.url.endsWith(`/${HorusEndpoints.refresh}`)
    ) {
      return next.handle(req);
    }

    let authReq = req;
    if (isPlatformApi || isLprV2 || isLprDashboardApi) {
      authReq = this.addPlatformAuth(req);
    } else if (isHorusApi) {
      authReq = this.addHorusAuth(req);
    }

    return next.handle(authReq).pipe(
      catchError((error) => {
        if (error instanceof HttpErrorResponse && error.status === 401) {
          if (isPlatformApi || isLprV2 || isLprDashboardApi) {
            return this.handle401Error(authReq, next);
          } else if (isHorusApi) {
            return this.handle401HorusError(authReq, next);
          }
        }
        return throwError(() => error);
      }),
    );
  }

  private isLoginFlowRequest(url: string): boolean {
    const path = url.slice(environment.baseUrl.length).split('?')[0];
    const loginPath = `/${AuthEndpoints.login}`;
    return path === loginPath || path.startsWith(`${loginPath}/`);
  }

  private addPlatformAuth(req: HttpRequest<any>): HttpRequest<any> {
    const accessToken = this.authService.localStorageToken();
    if (accessToken) {
      return req.clone({
        headers: req.headers.set('Authorization', `Bearer ${accessToken}`),
      });
    }
    return req;
  }

  private addHorusAuth(req: HttpRequest<any>): HttpRequest<any> {
    const accessTokenHorus =
      this.authService.tokenHorus() ?? localStorage.getItem('tokenHorus');

    if (accessTokenHorus) {
      // The daily dashboard has its own platform filter (whitelabel_id).
      if (req.url === `${environment.horusBaseUrl}/api/v1/recorders/daily/`) {
        return req.clone({
          headers: req.headers.set(
            'Authorization',
            `Bearer ${accessTokenHorus}`,
          ),
        });
      }
      let modifiedBody = req.body;
      let modifiedParams = req.params;
      const me = this.me();

      if (req.method === 'GET') {
        if (me?.whitelabel?.id && !modifiedParams.has('client_id')) {
          modifiedParams = modifiedParams.set(
            'client_id',
            me.whitelabel.id.toString(),
          );
        }

        if (me?.id && !modifiedParams.has('user_id')) {
          modifiedParams = modifiedParams.set('user_id', me.id);
        }
      }

      if (req.method === 'GET' && me?.id && !modifiedParams.has('user_id')) {
        modifiedParams = modifiedParams.set('user_id', String(me.id));
      }

      if (
        req.body &&
        typeof req.body === 'object' &&
        !Array.isArray(req.body)
      ) {
        if (typeof FormData !== 'undefined' && req.body instanceof FormData) {
          const fd = req.body as FormData;
          const newFd = new FormData();
          fd.forEach((value, key) => newFd.append(key, value as any));
          if (me?.id) newFd.append('user_id', String(me.id));
          if (me?.name) newFd.append('user_name', me.name);
          if (me?.whitelabel?.id)
            newFd.append('client_id', me.whitelabel.id.toString());
          modifiedBody = newFd;
        } else {
          const isBinary =
            (typeof File !== 'undefined' && req.body instanceof File) ||
            (typeof Blob !== 'undefined' && req.body instanceof Blob) ||
            (typeof ArrayBuffer !== 'undefined' &&
              req.body instanceof ArrayBuffer);

          if (!isBinary) {
            if (me?.id || me?.name) {
              modifiedBody = {
                ...(req.body || {}),
                ...(me?.id ? { user_id: me.id } : {}),
                ...(me?.name ? { user_name: me.name } : {}),
                ...(me?.whitelabel?.id
                  ? { client_id: me.whitelabel.id.toString() }
                  : {}),
              };
            } else {
              modifiedBody = { ...(req.body || {}) };
            }
          } else {
            modifiedBody = req.body;
          }
        }
      }

      return req.clone({
        headers: req.headers.set('Authorization', `Bearer ${accessTokenHorus}`),
        params: modifiedParams,
        body: modifiedBody,
      });
    }
    return req;
  }

  private handle401Error(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    return this.refreshPlatformToken().pipe(
      switchMap((token) =>
        next
          .handle(
            req.clone({
              headers: req.headers.set('Authorization', `Bearer ${token}`),
            }),
          )
          .pipe(
            catchError((error) => {
              if (error instanceof HttpErrorResponse && error.status === 401) {
                this.endPlatformSession();
              }
              return throwError(() => error);
            }),
          ),
      ),
    );
  }

  private refreshPlatformToken(): Observable<string> {
    if (!this.platformRefresh$) {
      this.platformRefresh$ = this.authService.refreshAccessToken().pipe(
        map(({ refreshToken }) => refreshToken.token),
        catchError((error) => {
          this.endPlatformSession();
          return throwError(() => error);
        }),
        finalize(() => (this.platformRefresh$ = null)),
        share({ resetOnRefCountZero: false }),
      );
    }

    return this.platformRefresh$;
  }

  private endPlatformSession(): void {
    const currentUrl = this.router.url;

    if (isPublicAuthRoute(currentUrl, window.location.pathname)) {
      return;
    }

    this.authService.signOut('expired');

    const returnUrl = this.route.snapshot.queryParamMap.get('returnUrl');

    if (
      !currentUrl.includes('/reset-password') &&
      !currentUrl.includes('/confirm-email') &&
      !returnUrl?.includes('/mosaic-view')
    ) {
      this.router.navigate(['/login'], {
        queryParamsHandling: 'preserve',
      });
    }
  }

  private handle401HorusError(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    if (!this.isRefreshingHorus) {
      this.isRefreshingHorus = true;
      this.refreshTokenHorusSubject.next(null);

      return this.authService.refreshAccessTokenHorus().pipe(
        switchMap((tokenResponse) => {
          const newToken = tokenResponse.refresh;
          this.isRefreshingHorus = false;
          this.refreshTokenHorusSubject.next(newToken);

          const retryReq = this.addHorusAuth(
            req.clone({
              headers: req.headers.set('Authorization', `Bearer ${newToken}`),
            }),
          );

          return next.handle(retryReq);
        }),
        catchError((err) => {
          console.log('interceptor error ', err);

          this.isRefreshingHorus = false;
          return this.authService.loginHorus().pipe(
            switchMap(() => next.handle(this.addHorusAuth(req))),
            catchError((horusErr) => {
              console.error('Falha ao renovar token Horus:', horusErr);
              return throwError(() => horusErr);
            }),
          );
        }),
      );
    }

    return this.refreshTokenHorusSubject.pipe(
      filter((token) => token !== null),
      take(1),
      switchMap((token) =>
        next.handle(
          req.clone({
            headers: req.headers.set('Authorization', `Bearer ${token}`),
          }),
        ),
      ),
    );
  }
}
